import { addDays, parseISO, subDays, subMonths, subYears } from "date-fns";
import i18next from "i18next";

export const countriesList = [
  {
    name: "United States",
    alpha_2: "US",
  },
  {
    name: "Canada",
    alpha_2: "CA",
  },
  {
    name: "United Kingdom",
    alpha_2: "GB",
  },
  {
    name: "Australia",
    alpha_2: "AU",
  },
];

export const getUserCountry = (): string => i18next.language.split("-")[1];

export type Residence = {
  start_date: string;
  end_date: string | null;
  address: Partial<{
    country: string;
    postal_code: string;
  }>;
};

function getPeriodStart(requirement: {
  type: "months" | "years" | "days";
  value: number;
}) {
  return addDays(
    requirement.type === "years"
      ? subYears(Date.now(), requirement.value)
      : requirement.type === "months"
        ? subMonths(Date.now(), requirement.value)
        : subDays(Date.now(), requirement.value),
    1 // We want to include today in the period
  );
}

function getTransformedResidences(residences: Residence[], periodStart: Date) {
  return residences
    .map((residence) => ({
      ...residence,
      start_date: parseISO(residence.start_date),
      end_date: residence.end_date
        ? parseISO(residence.end_date)
        : new Date(Date.now()),
    }))
    .sort((a, b) => a.end_date.getTime() - b.end_date.getTime())
    .filter((residence) => residence.end_date >= periodStart);
}

export function checkResidencesMeetRequirementsForPeriod(
  residences: Residence[],
  requirement: {
    type: "months" | "years" | "days";
    value: number;
  }
) {
  const hasCurrentAddress = residences.some(({ end_date }) => !end_date);

  const periodStart = getPeriodStart(requirement);

  const transformedResidences = getTransformedResidences(
    residences,
    periodStart
  );

  const hasAResidenceThatStartedOnOrBeforePeriod = transformedResidences.some(
    (residence) => residence.start_date <= periodStart
  );

  const hasGaps = transformedResidences.some((residence, index) => {
    if (index === 0) {
      return false;
    }
    const previousResidence = transformedResidences[index - 1];
    return addDays(previousResidence.end_date, 1) < residence.start_date;
  });

  return (
    hasCurrentAddress && hasAResidenceThatStartedOnOrBeforePeriod && !hasGaps
  );
}

export function checkResidencesMeetRequirementsForCountry(
  residences: Residence[],
  when: {
    months?: number;
    days?: number;
  },
  countryCode: string
) {
  const periodStart = getPeriodStart(
    when.months
      ? { type: "months", value: when.months }
      : { type: "days", value: when.days || 1 }
  );
  const transformedResidences = getTransformedResidences(
    residences,
    periodStart
  );

  return transformedResidences.some(
    (residence) => residence.address.country === countryCode
  );
}
